/**
 * One line to read first. Findings and suggestions carry paragraphs — a
 * regulator's summary, a rationale that quotes the rule — and a reader
 * scanning a page wants the gist before the argument. These derive it: the
 * first sentence of the most decisive text, bounded so it stays one line.
 *
 * @module components/regulation/tldr
 */
import type { PolicyFinding, Recommendation } from "../../state/governance";
import { proposalLine } from "./policy/suggestionsView";

export const TLDR_MAX_LENGTH = 160;

/** The first sentence of a paragraph, ellipsised past the bound. */
export function firstSentence(text: string, max = TLDR_MAX_LENGTH): string {
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (collapsed.length === 0) return "";
  const first = collapsed.split(/(?<=[.!?])\s+(?=[A-Z0-9"“(])/)[0] ?? collapsed;
  if (first.length <= max) return first;
  const fragment = first.slice(0, max - 1);
  const boundary = fragment.lastIndexOf(" ");
  return `${(boundary > max / 2 ? fragment.slice(0, boundary) : fragment).trimEnd()}…`;
}

/**
 * A finding's gist: what it means for this document (the assessment), else
 * what the regulator published (the summary), with the count of required
 * changes when there are any.
 */
export function findingTldr(
  finding: Pick<PolicyFinding, "assessment" | "summary" | "requiredChanges">,
  { includeChangeCount = true }: { readonly includeChangeCount?: boolean } = {},
): string {
  const lead = firstSentence(finding.assessment ?? "") || firstSentence(finding.summary);
  const count = finding.requiredChanges.length;
  if (!includeChangeCount || count === 0 || lead.length === 0) return lead;
  return `${lead} · ${count} ${count === 1 ? "change" : "changes"} required`;
}

/** A suggestion's gist: the proposal itself when it fits a line, else why. */
export function suggestionTldr(
  suggestion: Pick<Recommendation, "kind" | "proposedChange" | "rationale">,
  max = TLDR_MAX_LENGTH,
): string {
  // A section reference says where to work; the amendment says what to do.
  // A company-profile suggestion is the same shape: the wording is the change.
  const prose =
    suggestion.kind === "control_amendment"
      ? "amendment"
      : suggestion.kind === "org_context"
        ? "contextAddition"
        : null;
  if (prose !== null) {
    const wording = (suggestion.proposedChange as Record<string, unknown>)[prose];
    if (typeof wording === "string" && wording.trim()) return firstSentence(wording, max);
  }
  const proposal = proposalLine(suggestion);
  if (proposal !== null && proposal.trim().length > 0) {
    const verb = suggestion.kind === "control_amendment" ? "Amend" : "Set to";
    return firstSentence(`${verb} ${proposal.trim()}`, max);
  }
  // This standalone inspector preamble records provenance, not the gap.
  // Skip only the observed phrase, and only when substantive text follows.
  const rationale = suggestion.rationale.replace(/^\s*New this inspection\.\s+(?=\S)/i, "");
  return firstSentence(rationale, max);
}
